// Org detail — the workhorse admin page. Overview + Users + Agents + Audit Log
// tabs, plus the "Open Org View" button that starts an impersonation session
// and redirects the browser to the target org's subdomain via one-time handoff.

import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api.js';
import ActionMenu from '../components/ActionMenu.jsx';

const ROLE_LABELS = {
  org_admin: 'Admin',
  org_ro: 'Read Only',
  verso_admin: 'Verso Admin',
  deactivated: 'Deactivated',
};

const ROLE_COLORS = {
  org_admin: 'bg-blue-100 text-blue-700',
  org_ro: 'bg-gray-100 text-gray-600',
  deactivated: 'bg-red-100 text-red-600',
};

const STATUS_COLORS = {
  active: 'bg-green-100 text-green-700',
  inactive: 'bg-gray-100 text-gray-500',
  unknown: 'bg-yellow-100 text-yellow-700',
};

const TABS = ['Overview', 'Users', 'Service Tiers', 'Integrations', 'Agents', 'Audit Log'];

function SummaryCard({ label, value, sub }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-white px-4 py-3">
      <div className="text-xs font-medium text-gray-500">{label}</div>
      <div className="mt-1 text-2xl font-bold text-gray-900">{value}</div>
      {sub && <div className="mt-0.5 text-xs text-gray-400">{sub}</div>}
    </div>
  );
}

function OverviewTab({ org }) {
  const agents = org.agents || [];
  const activeAgents = agents.filter((a) => a.status === 'active');

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <SummaryCard label="Agents" value={agents.length} sub={`${activeAgents.length} connected`} />
        <SummaryCard label="Users" value={org.user_count ?? 0} />
        <SummaryCard label="Created" value={new Date(org.created_at).toLocaleDateString()} />
        <SummaryCard label="Org ID" value={org.org_id} />
      </div>

      {agents.length > 0 && (
        <div className="rounded-lg border border-gray-200 bg-white">
          <div className="border-b border-gray-200 px-4 py-3">
            <h3 className="text-sm font-medium text-gray-900">Agent Status</h3>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-xs text-gray-500">
              <tr>
                <th className="px-4 py-2">Status</th>
                <th className="px-4 py-2">Name</th>
                <th className="px-4 py-2">Endpoint</th>
                <th className="px-4 py-2">Last Seen</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {agents.map((a) => (
                <tr key={a.agent_id}>
                  <td className="px-4 py-2">
                    <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[a.status] || STATUS_COLORS.unknown}`}>
                      {a.status || 'unknown'}
                    </span>
                  </td>
                  <td className="px-4 py-2 font-medium">{a.name}</td>
                  <td className="px-4 py-2 font-mono text-xs text-gray-500">{a.endpoint_url}</td>
                  <td className="px-4 py-2 text-gray-500">{a.last_seen ? new Date(a.last_seen).toLocaleString() : 'Never'}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function UsersTab({ org, orgId, queryClient }) {
  const users = org.users || [];
  const pendingInvitations = org.pending_invitations || [];
  const [showInvite, setShowInvite] = useState(false);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState('org_ro');
  const [deactivatingUser, setDeactivatingUser] = useState(null);
  const [editingUser, setEditingUser] = useState(null);       // user object or null
  const [editForm, setEditForm] = useState({ name: '', email: '', role: 'org_ro' });

  // Cross-org invite from the admin tier. Writes the same user_invitations
  // row shape the org-tier invitations endpoint uses — both tiers share D1,
  // so the invitation is picked up on the invitee's first login regardless
  // of which console minted it.
  const inviteMutation = useMutation({
    mutationFn: (data) => api.post(`/verso-admin/organizations/${orgId}/invite`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org-detail', orgId] });
      setShowInvite(false);
      setInviteEmail('');
      setInviteRole('org_ro');
    },
  });

  const deactivateMutation = useMutation({
    mutationFn: (userId) => api.delete(`/verso-admin/users/${userId}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org-detail', orgId] });
      setDeactivatingUser(null);
    },
  });

  // Edit user properties. Changing the email clears the CF Access binding so
  // the account re-attaches to the new address on next login — the modal
  // explains this so it isn't a surprise.
  const editMutation = useMutation({
    mutationFn: ({ userId, body }) => api.put(`/verso-admin/users/${userId}`, body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['org-detail', orgId] });
      setEditingUser(null);
    },
  });

  const resendMutation = useMutation({
    mutationFn: (inviteId) => api.post(`/verso-admin/organizations/${orgId}/invitations/${inviteId}/resend`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['org-detail', orgId] }),
  });
  const revokeMutation = useMutation({
    mutationFn: (inviteId) => api.delete(`/verso-admin/organizations/${orgId}/invitations/${inviteId}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['org-detail', orgId] }),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-gray-900">Organization Users</h3>
        <button
          onClick={() => setShowInvite(!showInvite)}
          className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700"
        >
          Invite User
        </button>
      </div>

      {showInvite && (
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <form
            onSubmit={(e) => { e.preventDefault(); inviteMutation.mutate({ email: inviteEmail, role: inviteRole }); }}
            className="flex items-end gap-3"
          >
            <div className="flex-1">
              <label className="block text-xs text-gray-500">Email</label>
              <input type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} required
                className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm focus:border-blue-500 focus:outline-none focus:ring-1 focus:ring-blue-500"
                placeholder="user@example.com" />
            </div>
            <div>
              <label className="block text-xs text-gray-500">Role</label>
              <select value={inviteRole} onChange={(e) => setInviteRole(e.target.value)}
                className="mt-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm">
                <option value="org_admin">Admin</option>
                <option value="org_ro">Read Only</option>
              </select>
            </div>
            <button type="submit" disabled={inviteMutation.isPending}
              className="rounded-md bg-blue-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">
              {inviteMutation.isPending ? 'Sending...' : 'Send Invite'}
            </button>
          </form>
          {inviteMutation.isError && (
            <p className="mt-2 text-sm text-red-600">{inviteMutation.error?.body?.error || inviteMutation.error?.message}</p>
          )}
        </div>
      )}

      {editingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-md rounded-lg bg-white p-6 shadow-xl">
            <h3 className="text-lg font-bold text-gray-900">Edit User</h3>
            <div className="mt-4 space-y-3">
              <div>
                <label className="block text-xs text-gray-500">Name</label>
                <input value={editForm.name} onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm" />
              </div>
              <div>
                <label className="block text-xs text-gray-500">Email</label>
                <input type="email" value={editForm.email} onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm" />
                {editForm.email.trim().toLowerCase() !== (editingUser.email || '').toLowerCase() && (
                  <p className="mt-1 text-xs text-amber-700">
                    Changing the email re-binds sign-in: the user logs in with the new address next time.
                  </p>
                )}
              </div>
              <div>
                <label className="block text-xs text-gray-500">Role</label>
                <select value={editForm.role} onChange={(e) => setEditForm((f) => ({ ...f, role: e.target.value }))}
                  className="mt-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm">
                  <option value="org_admin">Admin</option>
                  <option value="org_ro">Read Only</option>
                </select>
                {editingUser.role === 'deactivated' && (
                  <p className="mt-1 text-xs text-gray-500">This user is deactivated — saving a role re-activates them.</p>
                )}
              </div>
            </div>
            {editMutation.isError && (
              <p className="mt-3 text-sm text-red-600">{editMutation.error?.body?.error || editMutation.error?.message}</p>
            )}
            <div className="mt-5 flex justify-end gap-3">
              <button onClick={() => { setEditingUser(null); editMutation.reset(); }}
                className="rounded-md border border-gray-300 px-4 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
              <button
                onClick={() => editMutation.mutate({ userId: editingUser.user_id, body: {
                  name: editForm.name, email: editForm.email, role: editForm.role,
                } })}
                disabled={editMutation.isPending || !editForm.name.trim() || !editForm.email.includes('@')}
                className="rounded-md bg-blue-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">
                {editMutation.isPending ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </div>
      )}

      {deactivatingUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
          <div className="w-full max-w-sm rounded-lg bg-white p-6 shadow-xl">
            <h3 className="text-lg font-bold text-gray-900">Deactivate User</h3>
            <p className="mt-2 text-sm text-gray-600">
              Deactivate <span className="font-semibold">{deactivatingUser.name}</span>? They will lose all access immediately.
            </p>
            <div className="mt-4 flex justify-end gap-3">
              <button onClick={() => setDeactivatingUser(null)} className="rounded-md border border-gray-300 px-4 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50">Cancel</button>
              <button onClick={() => deactivateMutation.mutate(deactivatingUser.user_id)} disabled={deactivateMutation.isPending}
                className="rounded-md bg-red-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50">
                {deactivateMutation.isPending ? 'Deactivating...' : 'Deactivate'}
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="rounded-lg border border-gray-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs text-gray-500">
            <tr>
              <th className="px-4 py-2">Status</th>
              <th className="px-4 py-2">Name</th>
              <th className="px-4 py-2">Email</th>
              <th className="px-4 py-2">Role</th>
              <th className="px-4 py-2">Last Login</th>
              <th className="px-4 py-2" />
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {pendingInvitations.length === 0 && users.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-400">No users in this organization</td></tr>
            )}
            {pendingInvitations.map((inv) => (
              <tr key={inv.invite_id} className="bg-amber-50/50">
                <td className="px-4 py-2 font-medium text-gray-500 italic">— pending —</td>
                <td className="px-4 py-2 text-gray-700">{inv.email}</td>
                <td className="px-4 py-2">
                  <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${ROLE_COLORS[inv.role] || 'bg-gray-100 text-gray-500'}`}>
                    {ROLE_LABELS[inv.role] || inv.role}
                  </span>
                </td>
                <td className="px-4 py-2 text-xs text-amber-700">
                  Invited {new Date(inv.created_at).toLocaleDateString()} ·{' '}
                  {inv.expired
                    ? <span className="font-medium text-red-600">expired {new Date(inv.expires_at).toLocaleDateString()}</span>
                    : <>expires {new Date(inv.expires_at).toLocaleDateString()}</>}
                </td>
                <td className="px-4 py-2 text-right whitespace-nowrap">
                  {/* One shared mutation serves every row — scope the pending
                      state to THIS invite (mutation.variables) or every Resend
                      button animates at once and it looks like a send-to-all. */}
                  <button onClick={() => resendMutation.mutate(inv.invite_id)}
                    disabled={resendMutation.isPending && resendMutation.variables === inv.invite_id}
                    className="rounded-md border border-blue-300 px-2.5 py-1 text-xs font-medium text-blue-600 hover:bg-blue-50 disabled:opacity-50">
                    {resendMutation.isPending && resendMutation.variables === inv.invite_id ? 'Sending…' : 'Resend'}
                  </button>
                  <button onClick={() => revokeMutation.mutate(inv.invite_id)}
                    disabled={revokeMutation.isPending && revokeMutation.variables === inv.invite_id}
                    className="ml-2 rounded-md border border-red-300 px-2.5 py-1 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50">
                    {revokeMutation.isPending && revokeMutation.variables === inv.invite_id ? 'Revoking…' : 'Revoke'}
                  </button>
                </td>
              </tr>
            ))}
            {users.map((u) => (
              <tr key={u.user_id}>
                <td className="px-4 py-2">
                  <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${
                    u.role === 'deactivated' ? 'bg-gray-100 text-gray-500' : 'bg-green-100 text-green-700'
                  }`}>
                    {u.role === 'deactivated' ? 'Deactivated' : 'Active'}
                  </span>
                </td>
                <td className="px-4 py-2 font-medium">{u.name}</td>
                <td className="px-4 py-2 text-gray-500">{u.email}</td>
                <td className="px-4 py-2">
                  <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${ROLE_COLORS[u.role] || 'bg-gray-100 text-gray-500'}`}>
                    {ROLE_LABELS[u.role] || u.role}
                  </span>
                </td>
                <td className="px-4 py-2 text-gray-500">{u.last_login ? new Date(u.last_login).toLocaleString() : 'Never'}</td>
                <td className="px-4 py-2 text-right">
                  <ActionMenu
                    title={`Actions for ${u.name}`}
                    items={[
                      {
                        label: 'Edit user',
                        onClick: () => {
                          setEditingUser(u);
                          setEditForm({ name: u.name || '', email: u.email || '', role: u.role === 'deactivated' ? 'org_ro' : u.role });
                          editMutation.reset();
                        },
                      },
                      ...(u.role !== 'deactivated' ? [
                        { divider: true },
                        { label: 'Deactivate', danger: true, onClick: () => setDeactivatingUser(u) },
                      ] : []),
                    ]}
                  />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {resendMutation.isSuccess && (
        <p className="text-xs text-green-700">
          Invitation re-sent to {resendMutation.data?.email}
          {resendMutation.data?.email_transport === 'none' && ' (email delivery unavailable — the invite still activates on their first sign-in)'}
          . Expires {resendMutation.data?.expires_at}.
        </p>
      )}
      {(resendMutation.isError || revokeMutation.isError) && (
        <p className="text-xs text-red-600">
          {resendMutation.error?.body?.error || revokeMutation.error?.body?.error || 'Action failed'}
        </p>
      )}
    </div>
  );
}


// ---- Service Tiers ---------------------------------------------------------
// Per-org feature packages, defined ONLY here (admin console). Org staff
// assign a tier per subscriber from the org dashboard; the org API enforces
// the tier's features and grays out anything not included.
function TierEditor({ catalog, tier, onSave, onCancel, saving, error }) {
  const [name, setName] = useState(tier?.name || '');
  const [description, setDescription] = useState(tier?.description || '');
  // Business or residential. Decides which service addresses may carry the
  // package: Commio requires a number's E911 registration type to agree with its
  // CNAM type, so the org dashboard's add-line flow offers only matching addresses.
  // A real field rather than something read out of the name — deriving facts from
  // naming conventions has broken live things here twice.
  const [type, setType] = useState(tier?.type || '');
  const [features, setFeatures] = useState(() => {
    const init = {};
    for (const [key, spec] of Object.entries(catalog)) {
      init[key] = tier?.features?.[key] ?? spec.default;
    }
    return init;
  });
  const set = (key, value) => setFeatures((f) => {
    const next = { ...f, [key]: value };
    // Cascade dependency gates: a boolean master (e.g. portal_access) toggling
    // off zeroes any number feature that dependsOn it; toggling on restores a
    // sane minimum. Mirrors the server-side coercion in validateFeatures.
    if (value === false) {
      for (const [k, spec] of Object.entries(catalog)) {
        if (spec.dependsOn === key && spec.type === 'number') next[k] = 0;
      }
    } else if (value === true) {
      for (const [k, spec] of Object.entries(catalog)) {
        if (spec.dependsOn === key && spec.type === 'number' && (next[k] ?? 0) < 1) {
          next[k] = spec.default ?? 1;
        }
      }
    }
    return next;
  });

  return (
    <div className="rounded-lg border border-blue-200 bg-blue-50/30 p-4 space-y-4">
      <div className="grid gap-3 sm:grid-cols-2">
        <div>
          <label className="block text-sm font-medium text-gray-700">Tier name</label>
          <input value={name} onChange={(e) => setName(e.target.value)}
                 placeholder="e.g. Residential Basic"
                 className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700">Description</label>
          <input value={description} onChange={(e) => setDescription(e.target.value)}
                 placeholder="Shown to org staff when assigning"
                 className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm" />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700" htmlFor="tier-type">
            Service type
          </label>
          <select id="tier-type" value={type} onChange={(e) => setType(e.target.value)}
                  className="mt-1 w-full rounded-md border border-gray-300 px-3 py-1.5 text-sm">
            <option value="">No restriction</option>
            <option value="business">Business</option>
            <option value="residential">Residential</option>
          </select>
          <p className="mt-1 text-xs text-gray-500">
            Restricts which service addresses this package can be sold at — the carrier
            requires a number&rsquo;s 911 registration type to match. &ldquo;No
            restriction&rdquo; allows any address.
          </p>
        </div>
      </div>
      <div className="space-y-2">
        {Object.entries(catalog).map(([key, spec]) => (
          <div key={key} className="flex items-start gap-3 rounded-md bg-white px-3 py-2">
            <div className="flex-1">
              <div className="text-sm font-medium text-gray-800">{spec.label}</div>
              <div className="text-xs text-gray-400">{spec.description}</div>
            </div>
            {spec.type === 'boolean' && (
              <input type="checkbox" checked={Boolean(features[key])}
                     onChange={(e) => set(key, e.target.checked)} className="mt-1" />
            )}
            {spec.type === 'enum' && (
              <select value={features[key]} onChange={(e) => set(key, e.target.value)}
                      className="rounded-md border border-gray-300 px-2 py-1 text-sm">
                {spec.values.map((v) => <option key={v} value={v}>{v}</option>)}
              </select>
            )}
            {spec.type === 'number' && (() => {
              const gatedOff = spec.dependsOn && features[spec.dependsOn] === false;
              return (
                <input type="number" min={spec.min ?? 0}
                       value={gatedOff ? 0 : features[key]}
                       disabled={gatedOff}
                       title={gatedOff ? `Requires ${catalog[spec.dependsOn]?.label || spec.dependsOn}` : undefined}
                       onChange={(e) => set(key, Number(e.target.value))}
                       className="w-20 rounded-md border border-gray-300 px-2 py-1 text-sm disabled:cursor-not-allowed disabled:bg-gray-100 disabled:text-gray-400" />
              );
            })()}
          </div>
        ))}
      </div>
      {error && <p className="text-sm text-red-600">{error}</p>}
      <div className="flex gap-2">
        {/* type: '' is sent as null, which the API reads as "clear the restriction".
            Sending undefined would mean "leave it alone" and make unrestricting
            impossible. */}
        <button onClick={() => onSave({ name, description, type: type || null, features })}
                disabled={!name.trim() || saving}
                className="rounded-md bg-blue-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">
          {saving ? 'Saving…' : (tier ? 'Save tier' : 'Create tier')}
        </button>
        <button onClick={onCancel}
                className="rounded-md border border-gray-300 px-4 py-1.5 text-sm text-gray-700 hover:bg-white">
          Cancel
        </button>
      </div>
    </div>
  );
}

function featureSummary(catalog, features) {
  return Object.entries(catalog).map(([key, spec]) => {
    const v = features?.[key];
    if (spec.type === 'boolean') return v ? spec.label : null;
    if (spec.type === 'enum')    return v && v !== 'none' ? `${spec.label}: ${v}` : null;
    if (spec.type === 'number')  return v > (spec.min ?? 1) ? `${spec.label}: ${v}` : null;
    return null;
  }).filter(Boolean).join(' · ') || 'No features enabled';
}

function IntegrationsTab({ orgId }) {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['org', orgId, 'integrations'],
    queryFn: () => api.get(`/verso-admin/organizations/${orgId}/integrations`),
  });
  const toggle = useMutation({
    mutationFn: ({ id, enabled }) =>
      api.put(`/verso-admin/organizations/${orgId}/integrations/${id}`, { enabled }),
    onSuccess: (res) => queryClient.setQueryData(['org', orgId, 'integrations'], res),
  });

  const integrations = data?.integrations || [];

  return (
    <div className="rounded-lg border border-gray-200 bg-white p-5 shadow-sm">
      <h2 className="text-base font-semibold text-gray-900">Integrations</h2>
      <p className="mt-1 text-sm text-gray-500">
        Turn each integration on or off for this organization. Disabling one hides it
        from the org’s Settings → Integrations and blocks its use. Credentials the org
        already saved are preserved and take effect again when re-enabled.
      </p>
      {isLoading ? (
        <p className="mt-4 text-sm text-gray-400">Loading…</p>
      ) : (
        <ul className="mt-4 divide-y divide-gray-100">
          {integrations.map((i) => (
            <li key={i.id} className="flex items-center justify-between py-3">
              <div>
                <div className="text-sm font-medium text-gray-900">{i.label}</div>
                <div className="text-xs text-gray-400">
                  {i.kind === 'credential' ? 'Configured with credentials by the org' : 'Feature — no credentials'}
                </div>
              </div>
              <button
                type="button"
                onClick={() => toggle.mutate({ id: i.id, enabled: !i.enabled })}
                disabled={toggle.isPending}
                role="switch"
                aria-checked={i.enabled}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors disabled:opacity-50 ${
                  i.enabled ? 'bg-green-500' : 'bg-gray-300'
                }`}
                title={i.enabled ? 'Enabled — click to disable' : 'Disabled — click to enable'}
              >
                <span className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
                  i.enabled ? 'translate-x-5' : 'translate-x-1'
                }`} />
              </button>
            </li>
          ))}
        </ul>
      )}
      {toggle.isError && (
        <p className="mt-3 text-sm text-red-600">{toggle.error?.body?.error || 'Failed to update toggle'}</p>
      )}
    </div>
  );
}

function ServiceTiersTab({ orgId }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(null);   // 'new' | tier object | null
  const { data, isLoading } = useQuery({
    queryKey: ['org-tiers', orgId],
    queryFn: () => api.get(`/verso-admin/organizations/${orgId}/service-tiers`),
  });
  const catalog = data?.catalog || {};
  const tiers = data?.tiers || [];

  const save = useMutation({
    mutationFn: ({ tierId, body }) => tierId
      ? api.put(`/verso-admin/organizations/${orgId}/service-tiers/${tierId}`, body)
      : api.post(`/verso-admin/organizations/${orgId}/service-tiers`, body),
    onSuccess: () => {
      setEditing(null);
      queryClient.invalidateQueries({ queryKey: ['org-tiers', orgId] });
    },
  });
  const remove = useMutation({
    mutationFn: (tierId) => api.delete(`/verso-admin/organizations/${orgId}/service-tiers/${tierId}`),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['org-tiers', orgId] }),
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-sm text-gray-500">
          Feature packages for this org. Org staff assign a tier per subscriber;
          the platform enforces it (blocked API mutations, grayed-out dashboard
          controls, hidden portal features).
        </p>
        {editing === null && (
          <button onClick={() => setEditing('new')}
                  className="rounded-md bg-blue-600 px-4 py-1.5 text-sm font-medium text-white hover:bg-blue-700">
            New tier
          </button>
        )}
      </div>

      {editing !== null && (
        <TierEditor
          catalog={catalog}
          tier={editing === 'new' ? null : editing}
          saving={save.isPending}
          error={save.isError ? (save.error?.body?.error || save.error?.message) : null}
          onSave={(body) => save.mutate({ tierId: editing === 'new' ? null : editing.id, body })}
          onCancel={() => { setEditing(null); save.reset(); }}
        />
      )}

      {isLoading ? (
        <div className="rounded-lg bg-white p-6 text-center text-sm text-gray-500 shadow">Loading…</div>
      ) : tiers.length === 0 && editing === null ? (
        <div className="rounded-lg bg-white p-6 text-center text-sm text-gray-500 shadow">
          No service tiers yet — create the org&rsquo;s packages here (e.g. Residential Basic, Business Premium).
        </div>
      ) : (
        <div className="divide-y divide-gray-100 rounded-lg bg-white shadow">
          {tiers.map((t) => (
            <div key={t.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-gray-900">{t.name}</span>
                  {t.type && (
                    <span className="rounded-full bg-indigo-100 px-2 py-0.5 text-xs font-medium text-indigo-700">
                      {t.type}
                    </span>
                  )}
                  <span className="text-xs text-gray-400">
                    {t.subscriber_count ?? 0} subscriber{(t.subscriber_count ?? 0) === 1 ? '' : 's'}
                  </span>
                </div>
                {t.description && <div className="text-xs text-gray-500">{t.description}</div>}
                <div className="mt-0.5 text-xs text-gray-400">{featureSummary(catalog, t.features)}</div>
              </div>
              <button onClick={() => { setEditing(t); save.reset(); }}
                      className="rounded-md border border-gray-300 px-3 py-1 text-xs text-gray-700 hover:bg-gray-50">
                Edit
              </button>
              <button onClick={() => remove.mutate(t.id)}
                      disabled={remove.isPending || (t.subscriber_count ?? 0) > 0}
                      title={(t.subscriber_count ?? 0) > 0 ? 'Reassign subscribers first' : 'Delete tier'}
                      className="rounded-md border border-red-200 px-3 py-1 text-xs text-red-600 hover:bg-red-50 disabled:opacity-40">
                Delete
              </button>
            </div>
          ))}
        </div>
      )}
      {remove.isError && (
        <p className="text-sm text-red-600">{remove.error?.body?.error || remove.error?.message}</p>
      )}
    </div>
  );
}

function AgentsTab({ org }) {
  const agents = org.agents || [];

  return (
    <div className="rounded-lg border border-gray-200 bg-white">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-left text-xs text-gray-500">
          <tr>
            <th className="px-4 py-2">Status</th>
            <th className="px-4 py-2">Name</th>
            <th className="px-4 py-2">Endpoint</th>
            <th className="px-4 py-2">Last Seen</th>
            <th className="px-4 py-2">Created</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {agents.length === 0 ? (
            <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-gray-400">No agents registered</td></tr>
          ) : agents.map((a) => (
            <tr key={a.agent_id}>
              <td className="px-4 py-2">
                <span className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${STATUS_COLORS[a.status] || STATUS_COLORS.unknown}`}>
                  {a.status || 'unknown'}
                </span>
              </td>
              <td className="px-4 py-2 font-medium">{a.name}</td>
              <td className="px-4 py-2 font-mono text-xs text-gray-500">{a.endpoint_url}</td>
              <td className="px-4 py-2 text-gray-500">{a.last_seen ? new Date(a.last_seen).toLocaleString() : 'Never'}</td>
              <td className="px-4 py-2 text-gray-500">{a.created_at ? new Date(a.created_at).toLocaleDateString() : '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function AuditLogTab({ org }) {
  const allEntries = org.audit_log || [];
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');

  const entries = allEntries.filter((e) => {
    if (startDate && e.created_at < startDate) return false;
    if (endDate && e.created_at > endDate + 'T23:59:59') return false;
    return true;
  });

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-4">
        <div>
          <label className="block text-xs text-gray-500">From</label>
          <input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)}
            className="mt-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm" />
        </div>
        <div>
          <label className="block text-xs text-gray-500">To</label>
          <input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)}
            className="mt-1 rounded-md border border-gray-300 px-3 py-1.5 text-sm" />
        </div>
        {(startDate || endDate) && (
          <button onClick={() => { setStartDate(''); setEndDate(''); }}
            className="mt-5 text-xs text-gray-400 hover:text-gray-600">Clear</button>
        )}
        <span className="mt-5 text-xs text-gray-400">{entries.length} entries</span>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white">
        <table className="w-full text-sm">
          <thead className="bg-gray-50 text-left text-xs text-gray-500">
            <tr>
              <th className="px-4 py-2">Timestamp</th>
              <th className="px-4 py-2">Actor</th>
              <th className="px-4 py-2">Action</th>
              <th className="px-4 py-2">Target</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {entries.length === 0 ? (
              <tr><td colSpan={4} className="px-4 py-8 text-center text-sm text-gray-400">No audit log entries</td></tr>
            ) : entries.map((e) => (
              <tr key={e.id}>
                <td className="px-4 py-2 text-gray-500 whitespace-nowrap">{new Date(e.created_at).toLocaleString()}</td>
                <td className="px-4 py-2">{e.actor_name || e.actor_email || e.user_id}</td>
                <td className="px-4 py-2 font-mono text-xs">{e.action}</td>
                <td className="px-4 py-2 text-gray-500">{e.resource_type}{e.resource_id ? `: ${e.resource_id}` : ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      {/* The invite resend/revoke status messages that used to sit here were a
          copy-paste from UsersTab, where those mutations actually live. Nothing
          defines them in this component, so rendering the Audit Log tab threw a
          ReferenceError. eslint's no-undef had been reporting it. */}
    </div>
  );
}

export default function OrgDetail() {
  const { orgId } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [tab, setTab] = useState('Overview');

  const { data, isLoading } = useQuery({
    queryKey: ['org-detail', orgId],
    queryFn: () => api.get(`/verso-admin/organizations/${orgId}`),
  });

  const handoffMutation = useMutation({
    mutationFn: () => api.post('/verso-admin/handoff', { org_id: orgId }),
    onSuccess: (result) => {
      const d = result.data || result;
      // When the server returns a localhost URL but we're accessing from a
      // remote browser, rewrite the hostname so the handoff link is reachable.
      const handoff = new URL(d.url);
      if (handoff.hostname === 'localhost' && window.location.hostname !== 'localhost') {
        handoff.hostname = window.location.hostname;
      }
      window.open(handoff.toString(), '_blank');
    },
  });

  const org = data?.data || data;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="mx-auto h-8 w-8 animate-spin rounded-full border-4 border-blue-600 border-t-transparent" />
      </div>
    );
  }

  if (!org || !org.org_id) {
    return (
      <div className="rounded-lg bg-white p-8 text-center shadow">
        <h1 className="text-xl font-bold text-gray-900">Organization Not Found</h1>
        <button onClick={() => navigate('/')} className="mt-4 text-sm text-blue-600 hover:text-blue-800">&larr; Back to Organizations</button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <button onClick={() => navigate('/')} className="text-sm text-gray-400 hover:text-gray-600">&larr; Organizations</button>
          <h1 className="mt-1 text-2xl font-bold text-gray-900">{org.name}</h1>
          <p className="text-sm text-gray-500">{org.slug}</p>
        </div>
        <button
          onClick={() => handoffMutation.mutate()}
          disabled={handoffMutation.isPending}
          className="rounded-md border border-blue-300 px-4 py-2 text-sm font-medium text-blue-600 hover:bg-blue-50 disabled:opacity-50"
        >
          {handoffMutation.isPending ? 'Opening...' : 'Open Org View →'}
        </button>
      </div>

      {handoffMutation.isError && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
          {handoffMutation.error?.body?.error || handoffMutation.error?.message || 'Failed to open org view'}
        </div>
      )}

      <div className="border-b border-gray-200">
        <nav className="flex gap-6">
          {TABS.map((t) => (
            <button
              key={t}
              onClick={() => setTab(t)}
              className={`border-b-2 pb-2 text-sm font-medium transition-colors ${
                tab === t
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              {t}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'Overview' && <OverviewTab org={org} />}
      {tab === 'Users' && <UsersTab org={org} orgId={orgId} queryClient={queryClient} />}
      {tab === 'Service Tiers' && <ServiceTiersTab orgId={orgId} />}
      {tab === 'Integrations' && <IntegrationsTab orgId={orgId} />}
      {tab === 'Agents' && <AgentsTab org={org} />}
      {tab === 'Audit Log' && <AuditLogTab org={org} />}
    </div>
  );
}
